<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type StudentData = App.Students.Data.StudentData;

import type { HybridlyFormData } from '@/@types/global';
import type { Selectable } from '@/components/selectBox/selectBox';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import Divider from '@/components/divider/Divider.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FormCheckboxInput from '@/components/formCheckboxInput/FormCheckboxInput.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';

import EditAlt from '@/icons/line/edit-alt.svg';
import FormField from '@/components/formField/FormField.vue';
import { getBooleanDisplayValue } from '@/utils/filterBoolean';

const props = defineProps<{
  form: HybridlyFormData<{
    status: EnumData;
    date_submitted: string;
    css_profile: EnumData;
    pell_eligible: EnumData;
    is_alternative: boolean;
    selected_for_verification: EnumData;
  }>;
  financialAid?: FinancialAidData;
  isReadonly?: boolean;
  selectedForVerificationOptions: EnumData[];
  statuses: EnumData[];
  student?: StudentData;
  studentCssProfiles: EnumData[];
}>();

function handleStatusChange(status: Selectable<string> | null) {
  if (!status) {
    return;
  }

  props.form.fields.status = status;

  if (status.value !== props.form.initial.status.value) {
    props.form.fields.date_submitted = null;
    return;
  }

  props.form.fields.date_submitted = props.form.initial.date_submitted;
}
</script>

<template>
  <Fieldset title="Financial aid application status">
    <FormListboxInput
      :error="form.errors.status"
      :isDisabled="isReadonly"
      :modelValue="form.fields.status"
      :options="statuses"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.status?.label, financialAid?.status?.label)
          : undefined
      "
      label="Status"
      @update:modelValue="handleStatusChange"
    />
    <FormDateInput
      :clearable="false"
      :error="form.errors.date_submitted"
      :isDisabled="isReadonly"
      :modelValue="form.fields.date_submitted"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.date_submitted, financialAid?.date_submitted)
          : undefined
      "
      format="MMM dd, yyyy"
      label="Status Date"
      name="date_submitted"
      @update:modelValue="(value) => (form.fields.date_submitted = value)"
    />
    <FormListboxInput
      :error="form.errors.css_profile"
      :isDisabled="isReadonly"
      :modelValue="form.fields.css_profile"
      :options="studentCssProfiles"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.css_profile?.label, financialAid?.css_profile?.label)
          : undefined
      "
      isOptional
      label="CSS profile"
      @update:modelValue="(value) => (form.fields.css_profile = value)"
    />
    <FormListboxInput
      v-model="form.fields.selected_for_verification"
      :isDisabled="isReadonly"
      :error="form.errors.selected_for_verification"
      :options="selectedForVerificationOptions"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.selected_for_verification?.label,
              financialAid?.selected_for_verification?.label
            )
          : undefined
      "
      label="Selected for Verification"
    />
  </Fieldset>
  <Divider />
  <Fieldset title="Financial aid eligibility">
    <FormListboxInput
      :error="form.errors.pell_eligible"
      :isDisabled="isReadonly"
      :modelValue="form.fields.pell_eligible"
      :options="[
        {
          label: 'Not set',
          value: null
        },
        {
          label: 'Not eligible',
          value: false
        },
        {
          label: 'Eligible',
          value: true
        }
      ]"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.pell_eligible?.label,
              getBooleanDisplayValue(
                financialAid?.pell_eligible ?? null,
                'Not set',
                'Eligible',
                'Not Eligible'
              )
            )
          : undefined
      "
      label="Pell eligibility"
      @update:modelValue="(value) => (form.fields.pell_eligible = value)"
    />
    <FormField label="F/R price lunch eligibility">
      <Button
        :color="ButtonColor.secondary"
        :href="
          route('students.edit.enrollment-details', {
            student: student?.id
          }) + '#fr-price-lunch'
        "
        :iconLeft="EditAlt"
        :size="ButtonSize.xs"
        :variant="ButtonVariant.invisible"
        >Edit the F/R price lunch eligibility field on the Enrollment Details page</Button
      >
    </FormField>
  </Fieldset>
  <Divider />
  <Fieldset title="Others">
    <FormCheckboxInput
      :checked="form.fields.is_alternative"
      :error="form.errors.is_alternative"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.is_alternative, financialAid?.is_alternative)
          : undefined
      "
      label="Alternative financial aid"
      name="is_alternative"
      @update:checked="(value) => (form.fields.is_alternative = value)"
    />
  </Fieldset>
</template>
